import { useState, useCallback } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { fetchFastApi, ApiError } from "../api/fastapi";

// Match strategy types
export const stringMatchStrategies = [
  "exact_string",
  "levenshtein",
  "name_match",
  "id_match",
] as const;

export const numberMatchStrategies = [
  "exact_number",
  "percentile",
  "amount_match",
  "absolute_amount",
] as const;

export type StringMatchStrategy = (typeof stringMatchStrategies)[number];
export type NumberMatchStrategy = (typeof numberMatchStrategies)[number];
export type MatchStrategy = StringMatchStrategy | NumberMatchStrategy;
export type AttributeType = "string" | "number";

// Request body for create/update
export interface AttributeRequestBody {
  name: string;
  type: AttributeType;
  description?: string | null;
  match_strategy?: MatchStrategy | null;
  weight?: number | null;
  source_row_display?: boolean;
}

// Response schemas
const attributeResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  description: z.string().nullable(),
  matchStrategy: z.string().nullable(),
  fieldType: z.string().nullable(),
  weight: z.number().nullable(),
  sourceRowDisplay: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const createAttributeResponseSchema = z.object({
  message: z.string(),
  attribute: attributeResponseSchema,
});

const updateAttributeResponseSchema = z.object({
  message: z.string(),
  attribute: attributeResponseSchema,
});

const deleteAttributeResponseSchema = z.object({
  message: z.string(),
  attribute_id: z.string(),
});

const deleteAllAttributesResponseSchema = z.object({
  message: z.string(),
  deleted_count: z.number(),
});

const resetAttributesResponseSchema = z.object({
  message: z.string(),
});

export type ProcedureAttributeResponse = z.infer<
  typeof attributeResponseSchema
>;

// Response schema for bulk delete operations
const bulkDeleteResponseSchema = z.object({
  message: z.string(),
  deleted_count: z.number(),
});

interface UseProcedureAttributesOptions {
  procedureId: string;
  sessionId: string;
}

export function useProcedureAttributes({
  procedureId,
  sessionId,
}: UseProcedureAttributesOptions) {
  const [loading, setLoading] = useState(false);

  const createAttribute = useCallback(
    async (
      data: AttributeRequestBody,
    ): Promise<ProcedureAttributeResponse | null> => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          path: `/matching/procedures/${procedureId}/attributes`,
          method: "POST",
          body: { ...data },
        });

        const parsed = createAttributeResponseSchema.parse(response);
        toast.success(parsed.message);
        return parsed.attribute;
      } catch (error) {
        console.error("Error creating attribute:", error);
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error("Failed to create attribute");
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [procedureId],
  );

  const updateAttribute = useCallback(
    async (
      attributeId: string,
      data: AttributeRequestBody,
    ): Promise<ProcedureAttributeResponse | null> => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          path: `/matching/procedures/${procedureId}/attributes/${attributeId}`,
          method: "PUT",
          body: { ...data },
        });

        const parsed = updateAttributeResponseSchema.parse(response);
        toast.success(parsed.message);
        return parsed.attribute;
      } catch (error) {
        console.error("Error updating attribute:", error);
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error("Failed to update attribute");
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [procedureId],
  );

  const deleteAttribute = useCallback(
    async (attributeId: string): Promise<boolean> => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          path: `/matching/procedures/${procedureId}/attributes/${attributeId}`,
          method: "DELETE",
        });

        const parsed = deleteAttributeResponseSchema.parse(response);
        toast.success(parsed.message);
        return true;
      } catch (error) {
        console.error("Error deleting attribute:", error);
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error("Failed to delete attribute");
        }
        return false;
      } finally {
        setLoading(false);
      }
    },
    [procedureId],
  );

  const deleteAllAttributes = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    try {
      const response = await fetchFastApi({
        path: `/matching/procedures/${procedureId}/attributes`,
        method: "DELETE",
      });

      const parsed = deleteAllAttributesResponseSchema.parse(response);
      toast.success(parsed.message);
      return true;
    } catch (error) {
      console.error("Error deleting all attributes:", error);
      if (error instanceof ApiError) {
        toast.error(error.getUserMessage());
      } else {
        toast.error("Failed to delete all attributes");
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [procedureId]);

  const resetAttributes = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    try {
      const response = await fetchFastApi({
        path: `/matching/procedures/${procedureId}/reset-attributes`,
        method: "POST",
      });

      const parsed = resetAttributesResponseSchema.parse(response);
      toast.success(parsed.message);
      return true;
    } catch (error) {
      console.error("Error resetting attributes:", error);
      if (error instanceof ApiError) {
        toast.error(error.getUserMessage());
      } else {
        toast.error("Failed to reset attributes");
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [procedureId]);

  const dropSamples = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    try {
      const response = await fetchFastApi({
        path: `/matching/match-session/${sessionId}/source-rows`,
        method: "DELETE",
      });

      const parsed = bulkDeleteResponseSchema.parse(response);
      toast.success(parsed.message);
      return true;
    } catch (error) {
      console.error("Error dropping samples:", error);
      if (error instanceof ApiError) {
        toast.error(error.getUserMessage());
      } else {
        toast.error("Failed to drop samples");
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const dropAllDocs = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    try {
      const response = await fetchFastApi({
        path: `/matching/match-session/${sessionId}/documents`,
        method: "DELETE",
      });

      const parsed = bulkDeleteResponseSchema.parse(response);
      toast.success(parsed.message);
      return true;
    } catch (error) {
      console.error("Error dropping documents:", error);
      if (error instanceof ApiError) {
        toast.error(error.getUserMessage());
      } else {
        toast.error("Failed to drop documents");
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  return {
    loading,
    createAttribute,
    updateAttribute,
    deleteAttribute,
    deleteAllAttributes,
    resetAttributes,
    dropSamples,
    dropAllDocs,
  };
}
